import { useState, useEffect, useRef } from 'react';
import './customheader.css';
import { getAccessToken } from "../../services/authApi";
import { fieldReader } from "../../services/loanApi";
import { formatCurrency } from '../../utils/format'
import { FaLock, FaLockOpen, FaSyncAlt } from "react-icons/fa";
import { FiAlertTriangle } from "react-icons/fi";
import { info, error as logAppInsightError, warn as logAppInsightWarn } from '../../utils/logger';
import { logError } from "../../utils/logError";
import CustomHeaderFacebookLoader from './CustomHeaderFacebookLoader';
import {
    combinedHeaderFields,
    staticLoanFieldMap
} from './customHeaderFields';
import { runLicensingGateForPage } from '../../utils/licenseValidation';
import { LOAN_PROGRAM_CODES, isHeaderLtvVisible } from '../../config/organizations';
import { isLoanProgram } from '../../utils/loanProgramUtil';

const CustomHeader = () => {
    const [loanNumber, setloanNumber] = useState("");
    const [borrowerName, setborrowerName] = useState("");
    const [coborrowerName, setcoborrowerName] = useState("");
    const [companyName, setcompanyName] = useState("");
    const [propertyaddress, setpropertyaddress] = useState("");
    const [loanamount, setloanamount] = useState(0);
    const [interestRate, setInterestRate] = useState(0);
    const [loanPurpose, setLoanPurpose] = useState("");
    const [occupancy, setOccupancy] = useState("");
    const [headerLtv, setHeaderLtv] = useState("");
    const showHeaderLtv = isHeaderLtvVisible();
    const [amorType, setAmorType] = useState("");
    const [amorTerm, setAmorTerm] = useState("");
    const [monthlyPI, setMonthlyPI] = useState("");
    const [loading, setLoading] = useState(true);
    const [currentMilestone, setCurrentMilestone] = useState("");
    const [lockStatus, setLockStatus] = useState("");
    const [pollyStatus, setPollyStatus] = useState("");
    const [exceptionStatus, setExceptionStatus] = useState('');
    const [exceptionDate, setExceptionDate] = useState('');
    const [loanFileSequence, setLoanFileSequence] = useState(null);
    const headerRef = useRef(null);
    const tokenRef = useRef(null);
    const pollingRef = useRef(false);
    const regenLogOnceRef = useRef(false);
    const inactiveLoggedRef = useRef(false);
    const loanIdRef = useRef(null);
    const [loanID, setLoanID] = useState(null);
    const [showArrows, setShowArrows] = useState(false);
    const [loanProgramDisplay, setLoanProgram] = useState("");
    const [lockValidationStatus, setLockValidationStatus] = useState('');
    const [headerDTIDSCR, setHeaderDTIDSCR] = useState('');
    const [headerDTIDSCRLabel, setHeaderDTIDSCRLabel] = useState('');

    const [licenseBlocked, setLicenseBlocked] = useState(false);
    const [licenseMessage, setLicenseMessage] = useState('');
    const [isLockExpired, setIsLockExpired] = useState(false);
    const [lockExpirationDate, setLockExpirationDate] = useState('');
    const [licensingPageID, setLicensingPageID] = useState('');
    const [channelType, setChannelType] = useState('');

    const checkOverflow = () => {
        requestAnimationFrame(() => {
            if (headerRef.current) {
                const hasOverflow = headerRef.current.scrollWidth > headerRef.current.clientWidth;
                setShowArrows(hasOverflow);
            }
        });
    };

    useEffect(() => {
        const handleResize = () => checkOverflow();

        requestAnimationFrame(checkOverflow);

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (window.elli && window.elli.script && window.elli.script.guest) {
            window.elli.script.guest.create();
            initializeAuthObject();
        } else {
            console.warn("elli.script.guest is not available.");
        }
    }, []);

    const initializeAuthObject = async () => {
        try {
            window.elli.script.getObject("tpoApplication").then(async function (tpoApplicationObject) {
                const tpoAppObject = tpoApplicationObject;
                let attempts = 0;
                const maxAttempts = 10;
                const delay = 200;

                async function tryGetLoanHeader() {
                    try {
                        const loanSummary = await tpoAppObject.getLoanSummary();

                        if (loanSummary && loanSummary.loanId) {
                            info("Header loaded", {
                                loanid: loanSummary.loanId,
                                loanNumber: loanSummary.loanNumber || "N/A"
                            });
                            setLoanID(loanSummary.loanId);
                            loanIdRef.current = loanSummary.loanId;
                            console.log("Setting Loan Id: " + loanSummary.loanId);
                        } else if (attempts < maxAttempts) {
                            attempts++;
                            console.log("tryGetLoanHeader- attempts: " + attempts);
                            setTimeout(tryGetLoanHeader, delay);
                        } else {
                            const errorCode = logError("HEADER_LOAN_ID_FETCH_ERROR");
                            logAppInsightWarn("Loan header not available after max attempts", {
                                component: "CustomHeader",
                                problemId: errorCode,
                                errorCode,
                                errorMessage: `${errorCode} - Failed to get loan header after ${maxAttempts} attempts.`
                            });
                        }
                    } catch (error) {
                        console.error("Error in tryGetLoanHeader:", error);

                        if (attempts < maxAttempts) {
                            attempts++;
                            console.log("tryGetLoanHeader error - retry attempt: " + attempts);
                            setTimeout(tryGetLoanHeader, delay);
                        } else {
                            const errorCode = logError("HEADER_LOAN_ID_FETCH_ERROR");
                            logAppInsightWarn("Loan header retrieval failed", {
                                component: "CustomHeader",
                                problemId: errorCode,
                                errorCode,
                                rawError: error,
                                errorMessage: `${errorCode} - Failed to get loan header after ${maxAttempts} attempts with error: ${error.message}`
                            });
                        }
                    }
                }

                // Start the initial attempt
                tryGetLoanHeader();
            });
        } catch (error) {
            const errorCode = logError("HEADER_LOAN_ID_FETCH_ERROR");
            logAppInsightError("Header Initialization Failed", {
                component: "CustomHeader",
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - Failed to load Header Information.`
            });
        }
    };
    const loadData = async () => {
        try {
            const authObject = await window.elli.script.getObject("auth");
            const authCode = await authObject.createAuthCode();
            const token = await getAccessToken(authCode);
            const fieldData = await fieldReader(token, combinedHeaderFields, loanID);

            // Parse values
            const get = (id, def = "") => fieldData?.[id]?.trim?.() || def;
            const fullAddress = [get(staticLoanFieldMap.addressLine), get(staticLoanFieldMap.city), get(staticLoanFieldMap.state), get(staticLoanFieldMap.postalCode)].filter(Boolean).join(", ");

            setloanNumber(get(staticLoanFieldMap.loanNumber, "N/A"));
            setborrowerName(get(staticLoanFieldMap.borrowerName, "No borrower name entered"));
            setcoborrowerName(get(staticLoanFieldMap.coborrowerName, ""));
            setcompanyName(get(staticLoanFieldMap.companyName, "N/A"));
            setpropertyaddress(fullAddress || "No address entered");
            setInterestRate(get(staticLoanFieldMap.initialInterestRate, "0.000"));
            //setLoanType(get(staticLoanFieldMap.loanType, "N/A"));
            setLoanPurpose(get(staticLoanFieldMap.loanPurpose, "N/A"));
            setAmorType(get(staticLoanFieldMap.amortizationType, "N/A"));
            setAmorTerm(get(staticLoanFieldMap.amortizationTerm, "0"));
            setMonthlyPI(get(staticLoanFieldMap.monthlyPI, "0"));
            setLoanProgram(get(staticLoanFieldMap.loanProgram, "N/A"));
            setHeaderLtv(String(fieldData?.[staticLoanFieldMap.ltv] ?? "").trim());
            const hcltv = get(staticLoanFieldMap.hcltv, "0");

            const topRatio = get(staticLoanFieldMap.topRatio, "0");
            const bottomRatio = get(staticLoanFieldMap.bottomRatio, "0");
            //setDti(`${topRatio}% / ${bottomRatio}%`);
            setLockValidationStatus(get("4788", ""));
            const loanProgram = get("1401");
            const productName = get("CX.LOAN.PRODUCT.NAME");
            const topDTI = fieldData?.["740"] || 0;
            const bottomDTI = fieldData?.["742"] || 0;
            // Products that show a real DTI. Canonical codes, so this reads
            // the same whichever company's Encompass the loan came from -
            // ALLY (PROGRAM_001) is deliberately absent.
            const targetPrograms = [
                LOAN_PROGRAM_CODES.PROGRAM_004, // ACTIVATOR / SUNNY
                LOAN_PROGRAM_CODES.PROGRAM_006, // ACTIVATOR ITIN / SUNNY ITIN
                LOAN_PROGRAM_CODES.PROGRAM_007, // AMBASSADOR / PASSPORT
                LOAN_PROGRAM_CODES.PROGRAM_008, // SUPER JUMBO / STARRY
                LOAN_PROGRAM_CODES.PROGRAM_002, // ACCELERATOR / BREEZY
                LOAN_PROGRAM_CODES.PROGRAM_005, // ACCELERATOR ITIN / BREEZY ITIN
                LOAN_PROGRAM_CODES.PROGRAM_003, // ACCELERATOR INCOME / BREEZY INCOME
                LOAN_PROGRAM_CODES.PROGRAM_009, // ACTIVATOR PRIME / SUNNY SELECT
                LOAN_PROGRAM_CODES.PROGRAM_010, // ACCELERATOR EQUITY FIRST / CLOUDY
                LOAN_PROGRAM_CODES.PROGRAM_011, // ALLY EQUITY FIRST (ChampsTPO only)
                LOAN_PROGRAM_CODES.PROGRAM_013  // ACTIVATOR EQUITY FIRST (ChampsTPO only)
            ];

            const isTargetValue = isLoanProgram(productName, targetPrograms);
            const loanDocType = fieldData?.["CX.DOC.TYPE"] || "";
            const legalEntityType = fieldData?.['CX.LEGAL.ENTITY.TYPE'] || "";
            const dscrValue = Number(fieldData?.["CX.DSCR.CALCULATED"]) || 0;
            const isDSCR = loanDocType?.toLowerCase() === "dscr";
            const label = isDSCR ? "DSCR" : "DTI";
            const value = isDSCR
                ? dscrValue > 0 ? dscrValue : "N/A"
                : isTargetValue && loanProgram
                    ? `${topDTI}% / ${bottomDTI}%`
                    : "N/A";
            setHeaderDTIDSCR(value);
            setHeaderDTIDSCRLabel(label);
            await fetchMileStoneFields(fieldData);

            setLoading(false);

            const loanType = get(staticLoanFieldMap.loantype, "");
            const stateCode = get(staticLoanFieldMap.state, "");
            const city = get(staticLoanFieldMap.city, "");
            const tpoOrgId = get(staticLoanFieldMap.tpoOrgId, "");
            const tpoUserId = get(staticLoanFieldMap.tpoUserId, "");
            const tpoCorrUserId = get(staticLoanFieldMap.tpoCorrUserId, "");
            const finalUserId = channelType?.toLowerCase() == "correspondent" ? tpoCorrUserId : tpoUserId;
            fetchLicenseValidation(loanType, stateCode, tpoOrgId, finalUserId, city, legalEntityType);

        } catch (error) {
            const errorCode = logError("CUSTOM_HEADER_LOAD_ERROR");
            logAppInsightError("Custom Header Load Error", {
                component: "CustomHeader",
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - Failed to load custom header data.`
            });
        }
    };

    useEffect(() => {
        if (loanID) {
            loadData();
        }
    }, [loanID, channelType]);

    const fetchLicenseValidation = async (loanType, stateCode, tpoOrgId, tpoUserId, city, legalEntityType) => {
        try {
            //Skip licensing check if stateCode is empty/null/undefined
            if (!stateCode || stateCode.trim() === "") {
                setLicenseBlocked(false);
                setLicenseMessage('');
                return;
            }
            const licStatus = await runLicensingGateForPage(loanType, stateCode, tpoOrgId, tpoUserId, city, legalEntityType);

            if (licStatus && licStatus.blocked) {
                setLicenseBlocked(true);
                // prefer message, fallback to reason or a default string
                setLicenseMessage(licStatus.shortMessage || `${licStatus.stateName || 'Property'} state requires a License. Click for details.`);
            } else {
                setLicenseBlocked(false);
                setLicenseMessage('');
            }
        } catch (licError) {
            // If licensing check fails unexpectedly, don't block the header; log it and set a generic message
            const errorCode = logError("CUSTOM_HEADER_LICENSING_ERROR");
            logAppInsightError("Custom Header Licensing Error", {
                component: "CustomHeader",
                problemId: errorCode,
                errorCode,
                rawError: licError,
                errorMessage: `${errorCode} - Licensing check failed.`
            });
            // optional: show a non-blocking message
            setLicenseBlocked(false);
            setLicenseMessage('');
        }
    };

    const isDateExpiredInPacific = (dateVal) => {
        if (!dateVal) return false;
        const str = String(dateVal).trim();
        const [mm, dd, yyyy] = str.split('/').map(s => parseInt(s, 10));
        // expiration moment = end of that day in Pacific
        const expDate = new Date(yyyy, mm - 1, dd, 23, 59, 59);
        const pacificNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
        return expDate < pacificNow;
    };

    const getChannel = (response) => {
        const channel = response["2626"] || "";
        const delegated = response["TPO.X88"] || "";
        setChannelType(channel);

        // use environment variables for channel names to avoid hardcoding and allow flexibility, with fallbacks to original strings
        //REACT_APP_LOAN_CHANNEL_WHOLESALE="Banked - Wholesale"
        //REACT_APP_LOAN_CHANNEL_CORRESPONDENT="Correspondent"
        const wholesaleChannelName = process.env.REACT_APP_LOAN_CHANNEL_WHOLESALE || "Banked - Wholesale";
        const correspondentChannelName = process.env.REACT_APP_LOAN_CHANNEL_CORRESPONDENT || "Correspondent";
        const delegatedChannelName = process.env.REACT_APP_LOAN_CHANNEL_CORRESPONDENT_DELEGATED || "Correspondent Delegated";
        const nonDelegatedChannelName = process.env.REACT_APP_LOAN_CHANNEL_CORRESPONDENT_NON_DELEGATED || "Correspondent Non-Delegated";

        // do string comparisons to avoid case sensitivity issues from data inconsistencies, and handle null/undefined with default empty string
        if (channel.toLowerCase() === correspondentChannelName.toLowerCase() && delegated.toLowerCase() === "y") {
            setLicensingPageID(process.env.REACT_APP_LICENSING_DELEGATE);
            return delegatedChannelName;
        } else if (channel.toLowerCase() === correspondentChannelName.toLowerCase() && delegated.toLowerCase() === "n") {
            setLicensingPageID(process.env.REACT_APP_LICENSING_NONDELEGATE);
            return nonDelegatedChannelName;
        } else if (channel.toLowerCase() === wholesaleChannelName.toLowerCase()) {
            setLicensingPageID(process.env.REACT_APP_LICENSING_PAGE_EXTERNAL);
            return wholesaleChannelName;
        } else {
            return channel || "N/A";
        }
    };

    const fetchMileStoneFields = async (response) => {
        try {
            const channel = getChannel(response);
            const nonDelegated = channel === (process.env.REACT_APP_LOAN_CHANNEL_CORRESPONDENT_NON_DELEGATED || "Correspondent Non-Delegated");
            // format the current milestone name
            const cMilestone = response["Log.MS.LastCompleted"] || "";
            let finalMilestone = cMilestone;
            if (cMilestone) {
                if (cMilestone.toLowerCase().includes("started") && response["1401"] === "" && response["TPO.X90"] === "//") {
                    finalMilestone = "Loan Created";
                }
                else if (cMilestone.toLowerCase().includes("started") && response["1401"] !== "" && response["TPO.X90"] === "//") {
                    finalMilestone = "Not submitted";
                }
                else if (cMilestone.toLowerCase().includes("started") && response["1401"] !== "" && response["TPO.X90"] !== "//") {
                    finalMilestone = "Submitted";
                }
                else if (cMilestone.toLowerCase().includes("submitted to uw") && nonDelegated) {
                    finalMilestone = "Loan Pkg Submitted";
                } else if (cMilestone.toLowerCase().includes("submitted to uw")) {
                    finalMilestone = "Submitted to UW";
                } else if (cMilestone.toLowerCase().includes("initial approval") || cMilestone.toLowerCase().includes("conditional approval")) {
                    finalMilestone = "Conditional Approval";
                } else if (cMilestone.toLowerCase().includes("submit for condition review")) {
                    finalMilestone = "In for Condition Review";
                } else if (cMilestone.toLowerCase().includes("submit for final review")) {
                    finalMilestone = "Submitted for Final Review";
                } else if (cMilestone.toLowerCase().includes("ctc") ||
                    cMilestone.toLowerCase().includes("ready for docs") ||
                    cMilestone.toLowerCase().includes("docs ordered")) {
                    finalMilestone = "Clear to Close";
                } else if (cMilestone.toLowerCase().includes("docs out") ||
                    cMilestone.toLowerCase().includes("docs back")) {
                    finalMilestone = "Docs Out";
                } else if (cMilestone.toLowerCase().includes("funding") ||
                    cMilestone.toLowerCase().includes("ready for sale") ||
                    cMilestone.toLowerCase().includes("shipped") ||
                    cMilestone.toLowerCase().includes("loan sold") ||
                    cMilestone.toLowerCase().includes("final trailing docs") ||
                    cMilestone.toLowerCase().includes("purchased") ||
                    cMilestone.toLowerCase().includes("completion")) {
                    finalMilestone = "Funded";
                }
            }

            //const shouldShowMail = response["CX.TPO.LOANNOTES.UNREAD.BROK"] === "Y";
            //setShowMailIcon(shouldShowMail);
            const rawLockExp = response["762"] || "";
            setLockStatus(response["4209"] || "Not Locked");
            setPollyStatus(response["CX.LOAN.LOCK.STATUS"] || "");
            setLockExpirationDate(rawLockExp);

            // compute expiry in Pacific time
            const expired = isDateExpiredInPacific(rawLockExp);
            setIsLockExpired(expired);

            setCurrentMilestone(finalMilestone || "");
            // Set exception status, default to "Submitted" if empty
            const excStatus = response["CX.EXC.COMBINED.DECISION"];
            if (!excStatus?.trim() && exceptionDate && exceptionDate !== "//") {
                setExceptionStatus("Submitted");
            }
            else {
                setExceptionStatus(excStatus);
            }

            setExceptionDate(response["CX.EXC.PRE.REQ.DATE"]);
            setOccupancy(response["1811"] || "N/A");
            setLoanFileSequence(response["LOANFILESEQUENCENUMBER"]);
            setloanamount(response["2"] ?? formatCurrency(response["2"], "en-US", "USD"), 0);
            requestAnimationFrame(checkOverflow);
            setLoading(false);
        }
        catch (error) {
            const errorCode = logError("HEADER_MILESTONE_ERROR");

            logAppInsightError("Header MileStone Information Error", {
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - Failed to load Header MileStone Information.`,
                component: "header"
            });
        }
    };

    const [tooltip, setTooltip] = useState({ text: "", x: 0, y: 0, visible: false });

    const regenerateToken = async () => {
        // Avoid regeneration while tab is hidden or device is offline
        if (document.hidden || !navigator.onLine) {
            throw new Error('ENVIRONMENT_INACTIVE');
        }
        const tpoAuthObject = await window.elli.script.getObject("auth");
        const authCode = await tpoAuthObject.createAuthCode();
        return await getAccessToken(authCode);
    };


    useEffect(() => {
        if (!loanFileSequence || !loanIdRef.current || pollingRef.current) return;

        pollingRef.current = true;

        const fetchLoanFileSequence = async () => {
            try {
                // Do not poll when tab is hidden or device is offline
                if (document.hidden || !navigator.onLine) {
                    if (!inactiveLoggedRef.current) {
                        console.info('Polling paused: environment inactive (hidden/offline).');
                        inactiveLoggedRef.current = true;
                    }
                    return;
                }
                // Environment active; clear inactive log latch
                inactiveLoggedRef.current = false;
                if (!tokenRef.current) {
                    tokenRef.current = await regenerateToken();
                    // successful regeneration; clear one-time log latch
                    regenLogOnceRef.current = false;
                }

                const response = await fieldReader(tokenRef.current, ["LOANFILESEQUENCENUMBER"], loanIdRef.current);
                const currentSequence = response["LOANFILESEQUENCENUMBER"];

                if (currentSequence !== loanFileSequence) {
                    console.info(`LOANFILESEQUENCENUMBER changed: ${loanFileSequence} → ${currentSequence}`);
                    setLoanFileSequence(currentSequence);
                    loadData(); // This will also update loanIdRef again
                }
            } catch (error) {
                const status = error?.response?.status || error?.status;
                if (status === 401) {
                    console.warn("Token expired. Regenerating...");
                    try {
                        // Clear stale token and try again
                        tokenRef.current = null;
                        // Skip regeneration when hidden/offline
                        if (document.hidden || !navigator.onLine) {
                            if (!inactiveLoggedRef.current) {
                                console.info('Token regeneration skipped: environment inactive (hidden/offline).');
                                inactiveLoggedRef.current = true;
                            }
                            return;
                        }
                        tokenRef.current = await regenerateToken();
                        // success; reset error-log latch
                        regenLogOnceRef.current = false;
                    } catch (tokenError) {
                        // Suppress repetitive logs while hidden/offline or when environment inactive
                        if (document.hidden || !navigator.onLine || tokenError?.message === 'ENVIRONMENT_INACTIVE') {
                            if (!inactiveLoggedRef.current) {
                                console.info('Token regeneration skipped: environment inactive (hidden/offline).');
                                inactiveLoggedRef.current = true;
                            }
                            return;
                        }
                        if (!regenLogOnceRef.current) {
                            const errorCode = logError("CUSTOM_HEADER_TOKEN_REGEN_ERROR");
                            logAppInsightWarn("Token Regeneration Error", {
                                component: "CustomHeader",
                                problemId: errorCode,
                                errorCode,
                                rawError: tokenError,
                                errorMessage: `${errorCode} - Token regeneration failed during polling.`
                            });
                            regenLogOnceRef.current = true;
                        }
                    }
                } else {
                    // Suppress logs while hidden/offline; log once otherwise
                    if (document.hidden || !navigator.onLine) return;
                    if (!regenLogOnceRef.current) {
                        const errorCode = logError("CUSTOM_HEADER_POLLING_ERROR");
                        logAppInsightWarn("Custom Header Polling Error", {
                            component: "CustomHeader",
                            problemId: errorCode,
                            errorCode,
                            rawError: error,
                            errorMessage: `${errorCode} - Error occurred during polling for Loan File Sequence change.`
                        });
                        regenLogOnceRef.current = true;
                    }
                }
            }
        };

        const interval = setInterval(fetchLoanFileSequence, 15000); // Poll every 15 seconds

        return () => {
            pollingRef.current = false;
            clearInterval(interval);
        };
    }, [loanFileSequence]);

    const parseNumber = (val) => parseFloat((val || "0").replace(/,/g, '')).toFixed(2);

    const handleRefresh = async () => {
        setLoading(true);
        loadData();
        setLoading(false);
    };

    const redirectToLockComparisonPage = async () => {
        const tpoAppObject = await window.elli.script.getObject("tpoApplication");
        await tpoAppObject.pageRedirect("ExternalLoanPage", {
            id: loanID,
            extpageid: process.env.REACT_APP_LOCK_COMPARISON_PAGE_ID,
            pagename: "LockComparison"
        });
    };

    const redirectToLicensingPage = async () => {
        const tpoAppObject = await window.elli.script.getObject("tpoApplication");
        await tpoAppObject.pageRedirect("ExternalLoanPage", {
            id: loanID,
            extpageid: licensingPageID,
            pagename: "Licensing"
        });
    };


    if (loading) {
        return <CustomHeaderFacebookLoader />;
    }

    return (
        <div className="custom-loan-header-container">
            <div className="custom-loan-header-content">
                <div className="loan-info">
                    <div className="loan-info-content">
                        <div className="loan-info-row">
                            <span className="borrower-name px-2 py-0.5 rounded-md">
                                {borrowerName}
                                {coborrowerName && ` & ${coborrowerName}`}
                            </span>
                        </div>
                        <div className="loan-info-row">
                            <span className="sub-address">{propertyaddress}</span>
                            {licenseBlocked && (
                                <FiAlertTriangle
                                    size={18}
                                    className="text-orange-600 cursor-pointer relative ml-2"
                                    style={{ marginTop: "-2px" }}
                                    onMouseEnter={(e) => {
                                        const tooltipX = e.clientX + 10;
                                        const tooltipY = e.clientY - 40;
                                        setTooltip({
                                            text: licenseMessage,
                                            x: tooltipX,
                                            y: tooltipY,
                                            visible: true,
                                        });
                                    }}
                                    onMouseLeave={() => setTooltip({ ...tooltip, visible: false })}
                                    onClick={redirectToLicensingPage}
                                />
                            )}
                        </div>
                        <div className="loan-info-row">
                            <span className="sub-address">{companyName}</span>
                        </div>
                    </div>
                </div>
                <div className="loan-info">
                    <div className="loan-info-content">
                        <div className="loan-info-row">
                            <span className="loan-info-label">Loan #</span>
                            <span className="loan-info-value">{loanNumber}</span>
                        </div>
                        <div className="loan-info-row">
                            <span className="loan-info-label">Total Loan Amount</span>
                            <span className="loan-info-value">{formatCurrency(parseNumber(loanamount), "en-US", "USD")}</span>
                        </div>
                        <div className="loan-info-row">
                            {/* <span className="loan-info-label">Loan Type</span>
                             <span className="loan-info-value">{loanType}</span> 
                            <span className="loan-info-value">Non QM</span> </div> */}
                            <span className="loan-info-label">Loan Program</span>
                            <span className="loan-info-value flex items-center gap-1">
                                {loanProgramDisplay}
                                {lockValidationStatus === "Needs Validation" && (
                                    <FiAlertTriangle
                                        size={18}
                                        className="text-orange-600 cursor-pointer relative"
                                        onMouseEnter={(e) => {
                                            const tooltipX = e.clientX + 10;
                                            const tooltipY = e.clientY - 40;
                                            setTooltip({
                                                text: "Re-Price: Click to compare changes",
                                                x: tooltipX,
                                                y: tooltipY,
                                                visible: true,
                                            });
                                        }}
                                        onMouseLeave={() => setTooltip({ ...tooltip, visible: false })}
                                        onClick={redirectToLockComparisonPage}
                                    />
                                )}

                            </span>
                        </div>
                    </div>
                </div>
                <div className="loan-info">
                    <div className="loan-info-content loan-info-metrics">
                        <div className="loan-info-row">
                            <span className="loan-info-label">Loan Purpose</span>
                            <span className="loan-info-value">{loanPurpose}</span>
                        </div>
                        <div className="loan-info-row">
                            <span className="loan-info-label">Occupancy</span>
                            <span className="loan-info-value">{occupancy}</span>
                            {showHeaderLtv && (
                                <>
                                    <span className="loan-info-label-dual">LTV</span>
                                    <span className="loan-info-value-dual">{headerLtv !== "" ? `${headerLtv} %` : "N/A"}</span>
                                </>
                            )}
                        </div>
                        <div className="loan-info-row">
                            <span className="loan-info-label">Interest Rate</span>
                            <span className="loan-info-value">{interestRate}%</span>
                            <span className="loan-info-label-dual">{headerDTIDSCRLabel}</span>
                            <span className="loan-info-value-dual">{headerDTIDSCR}</span>
                        </div>
                    </div>
                </div>
                <div className="loan-info">
                    <div className="loan-info-content">
                        <div className="loan-info-row">
                            <span className="loan-info-label">Term (Mos)</span>
                            <span className="loan-info-value">{amorTerm}</span>
                        </div>
                        <div className="loan-info-row">
                            <span className="loan-info-label">Mo. Pymt. (P&I)</span>
                            <span className="loan-info-value">${monthlyPI}</span>
                        </div>
                        <div className="loan-info-row">
                            <span className="loan-info-label">Amort. Type</span>
                            <span className="loan-info-value">{amorType}</span>
                        </div>
                    </div>
                </div>
                <div className="loan-info">
                    <div className="loan-info-content">
                        <div className="loan-info-row flex justify-between items-center pr-4">
                            <div className="flex items-center gap-2">
                                <span className="loan-info-label">Lock Status:</span>
                                <span>
                                    {lockValidationStatus === "Needs Validation" && (
                                        <FiAlertTriangle
                                            size={18}
                                            className="text-orange-600 cursor-pointer relative"
                                            onMouseEnter={(e) => {
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                const tooltipWidth = 220;
                                                const gap = 8;
                                                let x = Math.round(rect.left - tooltipWidth - gap);
                                                if (x < 8) {
                                                    x = Math.round(rect.right + gap);
                                                }

                                                const y = Math.round(rect.top);

                                                setTooltip({
                                                    text: "Re-Price: Click to compare changes",
                                                    x,
                                                    y,
                                                    visible: true,
                                                });
                                            }}
                                            onMouseLeave={() => setTooltip({ ...tooltip, visible: false })}
                                            onClick={redirectToLockComparisonPage}
                                        />
                                    )}
                                </span>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                    {isLockExpired ? (
                                        <>
                                            <FaLock
                                                style={{ marginLeft: 4 }}
                                                className="text-red-600"
                                                title="Expired"
                                            />
                                            <span style={{ color: '#b91c1c', marginLeft: 6, fontWeight: 600 }}>Expired</span>
                                        </>
                                    ) : pollyStatus?.toLowerCase().includes("initiallockrequestsubmitted") &&
                                        !(lockStatus && lockStatus.toLowerCase().includes("active lock")) ? (
                                        <FaLockOpen
                                            style={{ marginLeft: 4 }}
                                            className="text-yellow-500"
                                            title="Lock Requested"
                                        />
                                    ) : lockStatus && lockStatus.toLowerCase().includes("active lock") ? (
                                        <FaLock
                                            style={{ marginLeft: 4 }}
                                            className="text-orange-600"
                                            title="Locked"
                                        />
                                    ) : (
                                        <FaLockOpen
                                            style={{ marginLeft: 4 }}
                                            className="text-blue-400"
                                            title="Not Locked"
                                        />
                                    )}
                                </span>
                            </div>
                            <FaSyncAlt
                                size={14}
                                title="Refresh"
                                onClick={handleRefresh}
                                className="cursor-pointer text-green-700 hover:text-green-700 mr-2"
                            />
                        </div>

                        <div className="loan-info-row">
                            <span className="loan-info-label"> Loan Status:</span>
                            {currentMilestone && (
                                <span
                                    className={
                                        "loan-info-value px-2 py-0.5 rounded-md font-semibold" +
                                        (currentMilestone === "Funded"
                                            ? " bg-green-100 text-green-800"
                                            : " bg-blue-100 text-blue-800")
                                    }
                                >
                                    {currentMilestone}
                                </span>
                            )}
                        </div>
                        <div className="loan-info-row">
                            <span className="loan-info-label">Exception Status</span>
                            {/* {exceptionDate && exceptionDate !== '//' && exceptionStatus && ( */}
                            {exceptionStatus && (
                                <span
                                    className={`px-2 py-0.5 rounded-md font-semibold
                                        ${exceptionStatus.toLowerCase() === 'submitted' ? 'bg-orange-100 text-orange-800' :
                                            exceptionStatus.toLowerCase() === 'approved' ? 'bg-green-100 text-green-800' :
                                                exceptionStatus.toLowerCase() === 'denied' ? 'bg-red-100 text-red-800' :
                                                    exceptionStatus.toLowerCase() === 'conditioned' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-gray-100 text-gray-800'}`}
                                >
                                    {exceptionStatus?.charAt(0).toUpperCase() + exceptionStatus?.slice(1).toLowerCase()}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {tooltip.visible && (
                    <div
                        className="tooltip"
                        style={{
                            left: `${tooltip.x}px`,
                            top: `${tooltip.y + 10}px`,
                            opacity: 1,
                            position: "absolute"
                        }}
                    >
                        {tooltip.text}
                    </div>
                )}
            </div>


        </div>
    );

};

export default CustomHeader;
